import { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { useVerificationFieldContext } from '../../../context';
import { Camera } from '../camera';
import { DocumentFocus, FaceFocus } from '../components';
import { useLanguageContext } from 'Context';

export function useRenderCameraController(onError: (e: Error) => void) {
  const { setCurrentState, isDocument, isFace, setRawImage } =
    useVerificationFieldContext();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraRef = useRef<Camera | undefined>();
  const [loading, setLoading] = useState(true);
  const language = useLanguageContext();

  const support = useMemo(
    () => ({
      cancel: language?.getTranslate('ui.action.cancel'),
      take_a_photo: language?.getTranslate('ui.action.take_a_photo'),
    }),
    [language, language?.key],
  );

  const Focus = useMemo(() => {
    if (isDocument) {
      return DocumentFocus;
    }
    if (isFace) {
      return FaceFocus;
    }
    return FaceFocus;
  }, [isFace, isDocument]);

  const initCamera = useRef(async () => {
    if (canvasRef.current && videoRef.current) {
      cameraRef.current = await Camera.init(
        {
          canvas: canvasRef.current,
          video: videoRef.current,
        },
        onError,
      );
      setLoading(false);
    }
  });

  useEffect(() => {
    initCamera.current();

    return () => {
      cameraRef.current?.finish();
    };
  }, []);

  const changeView = useCallback(
    (e) => {
      e.preventDefault();
      cameraRef.current?.finish();
      setCurrentState('intro');
    },
    [setCurrentState],
  );

  const handlerConfirm = useCallback(
    async (e) => {
      e.preventDefault();
      if (cameraRef.current) {
        canvasRef.current?.toBlob((blob) => {
          if (blob) {
            cameraRef.current?.finish();
            setRawImage({
              file: blob,
              url: (window.URL || window.webkitURL).createObjectURL(blob),
            });
            setCurrentState('check');
          }
        });
      }
    },
    [canvasRef, setRawImage, setCurrentState],
  );

  return {
    changeView,
    canvasRef,
    videoRef,
    handlerConfirm,
    Focus,
    loading,
    support,
  };
}

export function useCameraController() {
  const [mediaError, setMediaError] = useState<
    | 'errorPermission'
    | 'errorCameraUsed'
    | 'errorDeviceLength'
    | 'errorResolution'
    | 'unknown'
  >();
  const language = useLanguageContext();

  const support = useMemo(
    () => ({
      cancel: language?.getTranslate('ui.action.cancel'),
      take_a_photo: language?.getTranslate('ui.action.take_a_photo'),
      errorPermission: language?.getTranslate('ui.media.errors.access'),
      errorCameraUsed: language?.getTranslate('ui.media.errors.already_use'),
      errorDeviceLength: language?.getTranslate('ui.media.errors.empty'),
      errorResolution: language?.getTranslate('ui.media.errors.resolution'),
      unknown: language?.getTranslate('ui.media.errors.unknown'),
    }),
    [language, language?.key],
  );

  const onError = useRef((err: Error) => {
    if (err) {
      if (
        err.name === 'NotAllowedError' ||
        err.name === 'PermissionDeniedError'
      ) {
        setMediaError('errorPermission');
      } else if (
        err.name === 'NotReadableError' ||
        err.name === 'TrackStartError'
      ) {
        setMediaError('errorCameraUsed');
      } else if (
        err.name === 'NotFoundError' ||
        err.name === 'DevicesNotFoundError'
      ) {
        setMediaError('errorDeviceLength');
      } else {
        setMediaError('unknown');
      }
    } else {
      setMediaError('unknown');
    }
  });

  const restart = useCallback(() => {
    setMediaError(undefined);
  }, []);

  return {
    mediaError,
    restart,
    onError,
    support,
  };
}
